/**
 * Product Variant Selector
 * Feature: 019-product-page-carousel - US3
 *
 * Provides dynamic variant selection with:
 * - O(1) variant lookup via hash map
 * - Instant price/availability updates
 * - URL parameter updates for sharing
 * - Carousel image switching for variant media
 * - ARIA live announcements for accessibility
 */

class VariantSelector {
  constructor(container) {
    if (!container) return;

    this.container = container;
    this.selectors = container.querySelectorAll('[data-option-select]');
    this.priceContainer = document.querySelector('[data-price-container]');
    this.availabilityContainer = document.querySelector('[data-product-availability]');
    this.priceAnnouncement = document.querySelector('[data-price-announcement]');
    this.variantIdInput = document.querySelector('[name="id"]');
    this.addToCartButton = document.querySelector('[data-add-to-cart-btn]');

    // Load variant data from JSON (T025)
    this.variantData = this.loadVariantData();
    if (!this.variantData || !this.variantData.variants) {
      console.error('Product variant data not found');
      return;
    }

    // Build hash map for O(1) variant lookup (T025)
    this.variantMap = this.buildVariantMap();

    // Store money format from Shopify (for price formatting)
    this.moneyFormat = window.theme?.moneyFormat || '${{amount}}';

    this.init();
  }

  /**
   * Load variant data from JSON script tag (T025)
   */
  loadVariantData() {
    const dataElement = document.querySelector('[data-product-variants]');
    if (dataElement) {
      try {
        return JSON.parse(dataElement.textContent);
      } catch (e) {
        console.error('Failed to parse product variant data:', e);
      }
    }
    return null;
  }

  /**
   * Build hash map for O(1) variant lookup (T025)
   * Maps "option1|option2|option3" -> variant object
   */
  buildVariantMap() {
    const map = new Map();

    this.variantData.variants.forEach(variant => {
      // Create key from options (max 3 options per Shopify)
      const key = [
        variant.option1,
        variant.option2,
        variant.option3
      ].filter(Boolean).join('|');

      map.set(key, variant);
    });

    return map;
  }

  /**
   * Initialize variant selector (T026)
   */
  init() {
    if (this.selectors.length === 0) return;

    // Add change listeners to all option selectors
    this.selectors.forEach(selector => {
      selector.addEventListener('change', () => this.handleOptionChange());
    });

    // Set initial state based on current variant
    this.updateInitialState();
  }

  /**
   * Set initial state from URL or first available variant
   */
  updateInitialState() {
    // Check for variant parameter in URL
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get('variant');

    if (variantId) {
      const variant = this.variantData.variants.find(v => v.id.toString() === variantId);
      if (variant) {
        this.updateSelectors(variant);
      }
    }
  }

  /**
   * Update selector dropdowns to match variant
   */
  updateSelectors(variant) {
    this.selectors.forEach(selector => {
      const position = parseInt(selector.dataset.optionPosition, 10);
      const value = variant[`option${position}`];
      if (value) {
        selector.value = value;
      }
    });
  }

  /**
   * Handle option change (T026)
   * Get selected options, find variant, update UI
   */
  handleOptionChange() {
    // Get currently selected options
    const selectedOptions = [];
    this.selectors.forEach(selector => {
      selectedOptions.push(selector.value);
    });

    // Find matching variant using hash map (O(1) lookup)
    const variant = this.findVariant(selectedOptions);

    if (variant) {
      // Update all UI elements
      this.updatePrice(variant);           // T027
      this.updateAvailability(variant);    // T028
      this.updateURL(variant);             // T029
      this.updateCarouselImage(variant);   // T030
      this.updateHiddenInput(variant);     // For add-to-cart form
    } else {
      // No matching variant found (invalid combination)
      this.showUnavailableState();
    }
  }

  /**
   * Find variant by selected options (T026)
   * Uses hash map for O(1) lookup
   */
  findVariant(selectedOptions) {
    const key = selectedOptions.join('|');
    return this.variantMap.get(key) || null;
  }

  /**
   * Update price display (T027)
   * Format with money filter and update DOM with sale/regular price
   */
  updatePrice(variant) {
    if (!this.priceContainer) return;

    const price = variant.price;
    const compareAtPrice = variant.compare_at_price;
    const onSale = compareAtPrice && compareAtPrice > price;

    // Format prices using Shopify money format
    const formattedPrice = this.formatMoney(price);
    const formattedCompare = compareAtPrice ? this.formatMoney(compareAtPrice) : null;

    // Update price elements
    const regularPrice = this.priceContainer.querySelector('[data-price-regular]');
    const salePrice = this.priceContainer.querySelector('[data-price-sale]');
    const comparePrice = this.priceContainer.querySelector('[data-price-compare]');

    if (onSale && salePrice && comparePrice) {
      // Show sale price
      salePrice.textContent = formattedPrice;
      comparePrice.querySelector('s').textContent = formattedCompare;

      // Toggle visibility classes
      const wrapper = this.priceContainer.querySelector('.product-price__wrapper');
      if (wrapper) {
        wrapper.classList.add('product-price__wrapper--sale');
      }
      if (regularPrice) regularPrice.style.display = 'none';
      salePrice.parentElement.style.display = '';
    } else if (regularPrice) {
      // Show regular price
      regularPrice.textContent = formattedPrice;

      // Toggle visibility classes
      const wrapper = this.priceContainer.querySelector('.product-price__wrapper');
      if (wrapper) {
        wrapper.classList.remove('product-price__wrapper--sale');
      }
      regularPrice.style.display = '';
      if (salePrice) salePrice.parentElement.style.display = 'none';
    }

    // Announce price change to screen readers (T032)
    this.announcePrice(formattedPrice, onSale, formattedCompare);
  }

  /**
   * Format price using Shopify money format or Intl.NumberFormat (T027)
   */
  formatMoney(cents) {
    // Shopify stores prices in cents
    const amount = (cents / 100).toFixed(2);

    // Use Shopify money format if available
    if (this.moneyFormat) {
      return this.moneyFormat.replace('{{amount}}', amount);
    }

    // Fallback to Intl.NumberFormat
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  }

  /**
   * Announce price change to screen readers (T032)
   */
  announcePrice(price, onSale, comparePrice) {
    if (!this.priceAnnouncement) return;

    let message = `Price: ${price}`;
    if (onSale) {
      message = `Sale price: ${price}, was ${comparePrice}`;
    }

    this.priceAnnouncement.textContent = message;
  }

  /**
   * Update availability display (T028)
   * Toggle "Sold out" message, disable/enable add-to-cart button
   */
  updateAvailability(variant) {
    if (!this.availabilityContainer) return;

    const available = variant.available;

    // Update availability badge
    const badge = this.availabilityContainer.querySelector('.product-info__availability-badge');
    if (badge) {
      if (available) {
        badge.textContent = window.theme?.translations?.inStock || 'In stock';
        badge.classList.remove('product-info__availability-badge--sold-out');
        badge.classList.add('product-info__availability-badge--in-stock');
      } else {
        badge.textContent = window.theme?.translations?.soldOut || 'Sold out';
        badge.classList.remove('product-info__availability-badge--in-stock');
        badge.classList.add('product-info__availability-badge--sold-out');
      }
    }

    // Enable/disable add-to-cart button (T044)
    if (this.addToCartButton) {
      this.addToCartButton.disabled = !available;

      if (!available) {
        this.addToCartButton.setAttribute('aria-disabled', 'true');
      } else {
        this.addToCartButton.removeAttribute('aria-disabled');
      }

      // Update button text (T044)
      const buttonText = this.addToCartButton.querySelector('[data-btn-text]');
      if (buttonText) {
        if (available) {
          buttonText.textContent = window.theme?.translations?.addToCart || 'Add to cart';
        } else {
          buttonText.textContent = window.theme?.translations?.soldOut || 'Sold out';
        }
      }
    }
  }

  /**
   * Update URL with variant parameter (T029)
   * Use history.replaceState for shareable links
   */
  updateURL(variant) {
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);

    // Update URL without page reload
    window.history.replaceState({}, '', url.toString());
  }

  /**
   * Update carousel to show variant image (T030)
   * Check variant.featured_media, call carousel.navigateToMediaId
   */
  updateCarouselImage(variant) {
    if (!variant.featured_media) return;

    // Access global carousel instance
    const carousel = window.productCarousel;
    if (carousel && typeof carousel.navigateToMediaId === 'function') {
      carousel.navigateToMediaId(variant.featured_media);
    }
  }

  /**
   * Update hidden variant ID input for add-to-cart form
   */
  updateHiddenInput(variant) {
    if (this.variantIdInput) {
      this.variantIdInput.value = variant.id;
    }
  }

  /**
   * Show unavailable state when no matching variant found
   */
  showUnavailableState() {
    // Show error message
    const errorElement = this.container.querySelector('[data-variant-error]');
    if (errorElement) {
      errorElement.hidden = false;
      errorElement.textContent = window.theme?.translations?.unavailable || 'This combination is unavailable';
    }

    // Disable add-to-cart button
    if (this.addToCartButton) {
      this.addToCartButton.disabled = true;
      this.addToCartButton.setAttribute('aria-disabled', 'true');
    }

    // Hide error after 3 seconds
    setTimeout(() => {
      if (errorElement) errorElement.hidden = true;
    }, 3000);
  }
}

/**
 * Initialize variant selector on page load
 */
document.addEventListener('DOMContentLoaded', function() {
  const variantContainer = document.querySelector('[data-variant-selectors]');

  if (variantContainer) {
    // Initialize variant selector
    const variantSelector = new VariantSelector(variantContainer);

    // Make accessible globally for debugging/testing
    window.variantSelector = variantSelector;
  }
});

/**
 * Export for module systems (if needed)
 */
if (typeof module !== 'undefined' && module.exports) {
  module.exports = VariantSelector;
}
